import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useBattleWordsStream } from '../../hooks/useBattleWordsStream';
import { CREATE_WORD } from '../../graphql/mutations';

const PHASES = [
  'Reading the theme…',
  'Brainstorming relevant words…',
  'Drafting example sentences…',
  'Checking for duplicates…',
];
const PHASE_INTERVAL_MS = 3500;

/**
 * AIBattleWordsSuggester — UI block above the topic chips in QuizLobby. Lets
 * the host type a theme (e.g. "Chinese New Year foods"), AI streams candidate
 * words, host clicks Add (creates a real Word in their library, tagged with
 * the per-session battle topic) or Drop (discards entirely).
 *
 * Lifecycle:
 *   - sessionTopic (e.g. "battle:1714329600123") is generated once on first Add
 *   - Each accepted word is tagged with sessionTopic via createWord
 *   - onSessionTopic(topic) lets the parent auto-select that topic for the battle
 *   - existingHanzi (string[]) is sent to the AI to avoid duplicates
 */
function AIBattleWordsSuggester({ defaultHSKLevel = 1, existingHanzi = [], onSessionTopic }) {
  const { status, words, thinking, error, submit, cancel, reset } = useBattleWordsStream();
  const [theme, setTheme] = useState('');
  const [hskLevel, setHskLevel] = useState(defaultHSKLevel);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [decided, setDecided] = useState({}); // idx → 'added' | 'dropped'
  const [addError, setAddError] = useState(null);
  const sessionTopicRef = useRef(null);

  const [createWord, { loading: creating }] = useMutation(CREATE_WORD);

  // Phase rotation while streaming.
  useEffect(() => {
    if (status !== 'streaming') return undefined;
    const id = setInterval(() => {
      setPhaseIdx((i) => Math.min(i + 1, PHASES.length - 1));
    }, PHASE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [status]);

  useEffect(() => {
    if (thinking && thinking.length > 0) {
      setPhaseIdx((i) => (i === 0 ? 1 : i));
    }
  }, [thinking]);

  const handleGenerate = (e) => {
    e?.preventDefault?.();
    if (!theme.trim()) return;
    setDecided({});
    setAddError(null);
    submit({
      theme: theme.trim(),
      hskLevel: Number(hskLevel) || 1,
      count: 5,
      existingWordHanzi: existingHanzi,
    });
  };

  const ensureSessionTopic = () => {
    if (sessionTopicRef.current) return sessionTopicRef.current;
    const topic = `battle:${Date.now()}`;
    sessionTopicRef.current = topic;
    if (onSessionTopic) onSessionTopic(topic);
    return topic;
  };

  const handleAdd = async (idx) => {
    const w = words[idx];
    if (!w) return;
    const sessionTopic = ensureSessionTopic();

    // Build CreateWordInput. Strip any audio fields the AI hallucinated (we
    // don't accept those) and ensure required fields are set.
    const cleanedMeanings = (w.meanings || []).map((m) => ({
      partOfSpeech: m.partOfSpeech || 'word',
      definition: m.definition || '',
      examples: (m.examples || []).map((ex) => ({
        cn: ex.cn || '',
        pinyin: ex.pinyin || '',
        en: ex.en || '',
      })),
    }));
    const baseTopics = Array.isArray(w.topics) ? w.topics : [];
    const input = {
      hanzi: w.hanzi,
      pinyin: w.pinyin,
      difficulty: 'beginner', // default; teacher can adjust later in admin
      hskLevel: w.hskLevel || Number(hskLevel) || 1,
      topics: [...new Set([...baseTopics, sessionTopic])],
      meanings: cleanedMeanings.length > 0 ? cleanedMeanings : [{ partOfSpeech: 'word', definition: '', examples: [] }],
    };

    setAddError(null);
    try {
      await createWord({ variables: { input } });
      setDecided((prev) => ({ ...prev, [idx]: 'added' }));
    } catch (err) {
      setAddError(err.message || 'Failed to add word');
    }
  };

  const handleDrop = (idx) => {
    setDecided((prev) => ({ ...prev, [idx]: 'dropped' }));
  };

  const addedCount = Object.values(decided).filter((v) => v === 'added').length;

  return (
    <div
      style={{
        marginBottom: 20,
        padding: 16,
        background: 'var(--paper-2)',
        color: 'var(--ink)',
        border: '1px solid var(--rule)',
        borderRadius: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <strong style={{ fontSize: 13 }}>✨ AI battle words</strong>
        <span className="t-small" style={{ color: 'var(--ink-3, #8b8779)', fontSize: 11 }}>
          · optional
        </span>
        {addedCount > 0 && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              fontFamily: 'var(--font-mono, monospace)',
              color: 'var(--ink-2, #6e6a5e)',
            }}
          >
            {addedCount} added → "{sessionTopicRef.current}"
          </span>
        )}
      </div>

      {/* NOTE: don't wrap this in a <form> — QuizLobby already renders a
          parent form for room creation. Nested <form> elements are invalid
          HTML and Enter / submit bubbles up, causing the outer form to fire
          and navigate the page. Buttons here use type="button" + onClick. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          className="input"
          placeholder='e.g. "Chinese New Year foods" or "verbs about cooking"'
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              handleGenerate();
            }
          }}
          style={{ flex: 1, fontSize: 13 }}
          maxLength={120}
          disabled={status === 'streaming'}
        />
        <select
          value={hskLevel}
          onChange={(e) => setHskLevel(e.target.value)}
          disabled={status === 'streaming'}
          style={{
            fontSize: 13,
            padding: '4px 8px',
            border: '1px solid var(--rule, #e3dfd2)',
            borderRadius: 4,
            background: 'white',
          }}
        >
          {[1, 2, 3, 4, 5, 6].map((lv) => (
            <option key={lv} value={lv}>HSK {lv}</option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={handleGenerate}
          disabled={!theme.trim() || status === 'streaming'}
        >
          {status === 'streaming' ? 'Generating…' : 'Generate'}
        </button>
        {(words.length > 0 || error) && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => { cancel(); reset(); setDecided({}); setAddError(null); }}
          >
            Clear
          </button>
        )}
      </div>

      {status === 'streaming' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'var(--ink-2, #6e6a5e)',
              animation: 'aiBattlePulse 1.4s ease-in-out infinite',
            }}
          />
          <span
            key={PHASES[phaseIdx]}
            className="ai-battle-phase"
            style={{
              fontFamily: 'var(--font-mono, ui-monospace, monospace)',
              fontSize: 11,
              color: 'var(--ink-3, #8b8779)',
              letterSpacing: '0.06em',
            }}
          >
            {PHASES[phaseIdx]}
          </span>
        </div>
      )}

      {error && (
        <p style={{ color: 'var(--vermillion, #c0392b)', fontSize: 12, marginBottom: 8 }}>{error}</p>
      )}
      {addError && (
        <p style={{ color: 'var(--vermillion, #c0392b)', fontSize: 12, marginBottom: 8 }}>
          Couldn't add to library: {addError}
        </p>
      )}

      {words.map((w, idx) => {
        const state = decided[idx];
        return (
          <div
            key={idx}
            style={{
              background: 'var(--paper)',
              color: 'var(--ink)',
              border: '1px solid var(--rule)',
              borderRadius: 6,
              padding: 12,
              marginBottom: 8,
              opacity: state ? 0.5 : 1,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontFamily: 'var(--font-han)', fontSize: 18, color: 'var(--ink)' }}>{w.hanzi}</span>
                  <span style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--ink-2)' }}>
                    {w.pinyin}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      padding: '1px 6px',
                      border: '1px solid var(--rule)',
                      borderRadius: 999,
                      color: 'var(--ink-3)',
                    }}
                  >
                    HSK {w.hskLevel}
                  </span>
                </div>
                {(w.meanings || []).slice(0, 1).map((m, mi) => (
                  <div key={mi} style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                    <em>{m.partOfSpeech}</em> · {m.definition}
                    {m.examples && m.examples[0] && (
                      <div style={{ fontSize: 11, marginTop: 2, color: 'var(--ink-3)' }}>
                        <span style={{ fontFamily: 'var(--font-han)' }}>{m.examples[0].cn}</span> — {m.examples[0].en}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {state === 'added' && (
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>✓ Added</span>
                )}
                {state === 'dropped' && (
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Dropped</span>
                )}
                {!state && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleDrop(idx)}
                      disabled={creating}
                      style={{
                        fontSize: 11,
                        padding: '4px 10px',
                        background: 'transparent',
                        border: '1px solid var(--rule)',
                        borderRadius: 4,
                        cursor: 'pointer',
                        color: 'var(--ink-2)',
                      }}
                    >
                      Drop
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAdd(idx)}
                      disabled={creating}
                      style={{
                        fontSize: 11,
                        padding: '4px 10px',
                        background: 'var(--ink)',
                        border: 0,
                        borderRadius: 4,
                        cursor: creating ? 'wait' : 'pointer',
                        color: 'var(--paper)',
                      }}
                    >
                      Add
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes aiBattlePulse {
          0%, 100% { opacity: 0.3; transform: scale(0.9); }
          50%      { opacity: 1;   transform: scale(1.15); }
        }
        .ai-battle-phase {
          animation: aiBattlePhaseFade 500ms ease-out both;
        }
        @keyframes aiBattlePhaseFade {
          from { opacity: 0; transform: translateY(2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default AIBattleWordsSuggester;
